import {
  calculateBlockedDuration,
  useUploadBlockedRecordFiles,
  BLOCKED_ITEM_FORM_CONDITIONS,
} from '@exam-center/blocked-user-management';
import { DateInputRenderer, FileInputRenderer } from '@exam-center/components';
import {
  BlockedRecordBlockedScope,
  GetBlockedRecordByBlockedUserIdDocument,
  GetBlockedUserByIdDocument,
  SearchBlockedUsersDocument,
  useGetAllEnabledExamCoursesQuery,
  useMarkExamPasserAsCheaterMutation,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormButton,
  FormSearchMultipleSelectionWithSelectAllOption,
  FormTextarea,
  SectionTitle,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, zodResolver } from '@shared/utils';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useBlockedRecordDetails } from '../../providers';

import { BlockedRecordRevocationSchema, type BlockedRecordRevocationSchemaType } from './schema';

interface BlockedRecordRevocationFormModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

export default function BlockedRecordRevocationFormModal(
  props: Readonly<BlockedRecordRevocationFormModalProps>,
) {
  const { open, onClose, onCompleted } = props;

  const { blockedUser, blockedRecord } = useBlockedRecordDetails();

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { data: examCoursesData, loading: isGettingExamCourses } =
    useGetAllEnabledExamCoursesQuery();

  const examCourseOptions = useMemo(() => {
    const examCourses = examCoursesData?.ecGetAllEnabledExamCourses.examCourses ?? [];
    const filteredExamCourses = examCourses
      .filter(
        (course) =>
          !blockedRecord.blockedRecordCourses.some(
            (blockedCourse) => blockedCourse.examCourse?.code === course.code,
          ),
      )
      .map((course) => ({
        label: `${course.code}: ${course.name}`,
        value: course.code,
      }));

    return filteredExamCourses;
  }, [examCoursesData, blockedRecord.blockedRecordCourses]);

  const { handleUpload, loading: isUploadingDocuments } = useUploadBlockedRecordFiles();

  const [markExamPasserAsCheater, { loading: isMarkingExamPasserAsCheater }] =
    useMarkExamPasserAsCheaterMutation();

  const formContext = useForm<BlockedRecordRevocationSchemaType>({
    resolver: zodResolver(BlockedRecordRevocationSchema),
    defaultValues: {
      blockedUntil: undefined,
      additionalCourses: [],
      fileAttachments: undefined,
      reason: undefined,
    },
  });
  const {
    watch,
    reset,
    formState: { isSubmitting },
  } = formContext;

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleResetForm = () => {
    reset();
  };

  const handleRevokeBlockedRecord = async (data: BlockedRecordRevocationSchemaType) => {
    const { additionalCourses, fileAttachments = [] } = data;

    const uploadedFileAttachments = await handleUpload(fileAttachments);

    const isSelectAllCourses = additionalCourses.find((course) => course.value === 'all');

    await markExamPasserAsCheater({
      variables: {
        markExamPasserAsCheaterInput: {
          blockedRecordId: blockedRecord.id,
          blockedUntil: data.blockedUntil,
          blockedReason: data.reason,
          blockedScope: isSelectAllCourses
            ? BlockedRecordBlockedScope.AllScope
            : BlockedRecordBlockedScope.PartialScope,
          additionalExamCourseCodes: isSelectAllCourses
            ? undefined
            : additionalCourses.map((course) => course.value),
          documentFileNames: uploadedFileAttachments,
        },
      },
      onCompleted: (data) => {
        onCompleted?.();
        showSnackbar({
          message: 'เพิ่มประเภทเป็นผู้ทุจริตเรียบร้อยแล้ว',
          variant: 'success',
        });
        setTimeout(() =>
          navigate('/admin/blocklist/:blockedUserId/:blockedRecordId', {
            params: {
              blockedUserId: blockedUser.id,
              blockedRecordId: data.ecMarkExamPasserAsCheater.data.cheaterBlockedRecord.id,
            },
          }),
        );
      },
      onError: () => {
        showSnackbar({
          message: 'เพิ่มประเภทเป็นผู้ทุจริตไม่สำเร็จ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        GetBlockedRecordByBlockedUserIdDocument,
        GetBlockedUserByIdDocument,
        SearchBlockedUsersDocument,
      ],
    });
  };

  const blockedUserFullName = `${blockedUser.title} ${blockedUser.firstName} ${blockedUser.middleName ?? ''} ${blockedUser.lastName}`;

  const watchedBlockedUntil = watch('blockedUntil');

  const blockedDuration = useMemo(() => {
    if (!watchedBlockedUntil) return '0 ปี';
    return calculateBlockedDuration(blockedRecord.blockedAt, watchedBlockedUntil);
  }, [blockedRecord.blockedAt, watchedBlockedUntil]);

  const isSubmittingForm = isSubmitting || isUploadingDocuments || isMarkingExamPasserAsCheater;

  return (
    <FormProvider {...formContext}>
      <Dialog
        maxWidth='md'
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
              event.stopPropagation();
              await formContext.handleSubmit(handleRevokeBlockedRecord)(event);
            },
          },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={handleClose} loading={isSubmittingForm}>
          ยืนยันการเพิ่มประเภทผู้ทุจริต
        </DialogTitle>
        <DialogContent dividers>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
            <Text>
              คุณกำลังเพิ่มประเภทการระงับสิทธิ์ให้กับ{' '}
              <Text variant='body1Semibold'>“{blockedUserFullName}”</Text> เป็น
              <Text variant='body1Semibold'>“ผู้ที่ทุจริต”</Text>
              <br />
              กรุณาตรวจสอบข้อมูลด้านล่างให้ถูกต้องก่อนดำเนินการ
            </Text>
            <Divider />
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
              <SectionTitle title='ข้อมูลการระงับสิทธิ์' />
              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                <DateInputRenderer
                  mode='view'
                  value={dayjs(blockedRecord.blockedAt).format('DD-MM-YYYY')}
                  slotProps={{
                    sx: { width: '212px' },
                    label: 'วันที่เริ่มระงับสิทธิ์',
                  }}
                />
                <Text variant='body1' sx={(theme) => ({ mt: theme.spacing(9) })}>
                  ถึง
                </Text>
                <Box sx={{ width: '212px' }}>
                  <DateInputRenderer
                    mode='input'
                    name='blockedUntil'
                    slotProps={{
                      label: 'วันที่สิ้นสุดระงับสิทธิ์',
                      minDate: dayjs(blockedRecord.blockedAt).toDate(),
                      placeholder: 'DD-MM-YYYY',
                      inputFormat: 'DD-MM-YYYY',
                      required: true,
                    }}
                  />
                </Box>
              </Flex>
              <Text>รวมระยะเวลาการระงับสิทธิ์: {blockedDuration}</Text>
            </Flex>
            <Divider />
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
              <SectionTitle title='หลักสูตรที่ระงับสิทธิ์สอบ' />
              <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2Semibold'>หลักสูตรปัจจุบันที่ถูกระงับสิทธิ์</Text>
                <Flex sx={(theme) => ({ gap: theme.spacing(1.5), my: theme.spacing(1.5) })}>
                  {blockedRecord.blockedRecordCourses.map((course) => (
                    <Chip
                      key={course.id}
                      label={`${course.examCourse?.code}: ${course.examCourse?.name}`}
                      size='small'
                      sx={(theme) => ({
                        fontWeight: 600,
                        width: 'fit-content',
                        px: theme.spacing(4),
                        py: theme.spacing(1.5),
                      })}
                    />
                  ))}
                </Flex>
              </Flex>
              <FormSearchMultipleSelectionWithSelectAllOption
                name='additionalCourses'
                label='เลือกหลักสูตรที่ต้องการระงับสิทธิ์สอบเพิ่ม'
                placeholder='เลือกหลักสูตรที่ต้องการระงับสิทธิ์สอบเพิ่ม'
                loading={isGettingExamCourses}
                options={examCourseOptions}
                multiple={true}
              />
            </Flex>
            <Divider />
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
              <SectionTitle title='เอกสารหลักฐานที่เกี่ยวข้อง' />
              <FileInputRenderer
                mode='input'
                name='fileAttachments'
                slotProps={{
                  label: 'เอกสารหลักฐานที่เกี่ยวข้อง',
                  placeholder: 'กรุณาอัปโหลดไฟล์ .JPG หรือ .JPEG หรือ .PNG หรือ .PDF',
                  accept: BLOCKED_ITEM_FORM_CONDITIONS.ALLOW_FILE_EXTENSIONS,
                }}
              />
              <FormTextarea
                name='reason'
                label='หมายเหตุเพิ่มเติม'
                placeholder='ระบุความคิดเห็นเพิ่มเติม'
              />
            </Flex>
          </Flex>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={handleClose}>
            ยกเลิก
          </Button>
          <FormButton label='เพิ่มเป็นผู้ทุจริต' color='error' disabled={isSubmittingForm} />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
